import { apiRequest } from '@/lib/api/client';
import type {
  AIConversation,
  AIMessage,
  AIDraft,
  ChatMessageResult,
  RecipientResolveResult,
  AITicket,
  KnowledgeSource,
  ScopeSnapshot,
  ContextualPromptResult,
  JournalSyncStatus,
} from './types';

export const superAiApi = {
  getPolicy: (roleCode?: string) =>
    apiRequest<ScopeSnapshot>('/ai/policy', {
      channel: 'platform',
      query: roleCode ? { role_code: roleCode } : undefined,
    }),

  createConversation: (title?: string, contextMode?: string, activeRole?: string) =>
    apiRequest<AIConversation>('/ai/conversations', {
      method: 'POST',
      channel: 'platform',
      body: { title: title ?? 'Диалог', context_mode: contextMode ?? 'auto' },
      query: activeRole ? { active_role: activeRole } : undefined,
    }),

  listConversations: (activeRole?: string) =>
    apiRequest<AIConversation[]>('/ai/conversations', {
      channel: 'platform',
      query: activeRole ? { active_role: activeRole } : undefined,
    }),

  getConversation: (id: string) =>
    apiRequest<AIConversation>(`/ai/conversations/${id}`, { channel: 'platform' }),

  listMessages: (conversationId: string) =>
    apiRequest<AIMessage[]>(`/ai/conversations/${conversationId}/messages`, {
      channel: 'platform',
    }),

  sendMessage: (
    conversationId: string,
    text: string,
    contextMode?: string,
    selectedContextFilter?: string,
  ) =>
    apiRequest<ChatMessageResult>(`/ai/conversations/${conversationId}/messages`, {
      method: 'POST',
      channel: 'platform',
      body: {
        text,
        context_mode: contextMode ?? 'auto',
        selected_context_filter: selectedContextFilter ?? null,
      },
    }),

  updateDraft: (draftId: string, payload?: Record<string, unknown>, draftStatus?: string) =>
    apiRequest<AIDraft>(`/ai/drafts/${draftId}`, {
      method: 'PUT',
      channel: 'platform',
      body: { payload: payload ?? null, status: draftStatus ?? null },
    }),

  approveDraft: (draftId: string) =>
    apiRequest<AIDraft>(`/ai/drafts/${draftId}/approve`, {
      method: 'POST',
      channel: 'platform',
    }),

  executeDraft: (draftId: string, recipientSelectionId?: string) =>
    apiRequest<{ action_id: string; draft_id: string; target_module: string; status: string; result: Record<string, unknown>; executed_at: string }>(
      `/ai/drafts/${draftId}/execute`,
      {
        method: 'POST',
        channel: 'platform',
        body: {
          draft_id: draftId,
          recipient_selection_id: recipientSelectionId ?? null,
          confirmed: true,
        },
      },
    ),

  resolveRecipients: (filters: {
    category?: string;
    department?: string;
    course_year?: number;
    study_program?: string;
    search?: string;
  }) =>
    apiRequest<RecipientResolveResult>('/ai/recipients/resolve', {
      method: 'POST',
      channel: 'platform',
      body: filters,
    }),

  confirmRecipients: (draftId: string, filters: Record<string, unknown>, selectedUserIds: string[]) =>
    apiRequest<{ selection_id: string; count: number; approved_at: string }>(
      '/ai/recipients/confirm',
      {
        method: 'POST',
        channel: 'platform',
        body: { draft_id: draftId, filters, selected_user_ids: selectedUserIds },
      },
    ),

  listTickets: (statusFilter?: string) =>
    apiRequest<AITicket[]>('/ai/tickets', {
      channel: 'platform',
      query: statusFilter ? { status_filter: statusFilter } : undefined,
    }),

  createTicket: (question: string, explanation?: string, conversationId?: string) =>
    apiRequest<AITicket>('/ai/tickets', {
      method: 'POST',
      channel: 'platform',
      body: {
        question,
        explanation: explanation ?? 'AI не смог подтвердить ответ',
        conversation_id: conversationId ?? null,
      },
    }),

  answerTicket: (ticketId: string, answer: string) =>
    apiRequest<AITicket>(`/ai/tickets/${ticketId}/answer`, {
      method: 'POST',
      channel: 'platform',
      body: { answer },
    }),

  listKnowledgeSources: (collection?: string, statusFilter?: string) =>
    apiRequest<KnowledgeSource[]>('/ai/knowledge-sources', {
      channel: 'platform',
      query: {
        ...(collection ? { collection } : {}),
        ...(statusFilter ? { status_filter: statusFilter } : {}),
      },
    }),

  createKnowledgeSource: (data: {
    collection: string;
    title: string;
    category: string;
    language: string;
    access_scope: string;
    department_id?: string | null;
    content_summary: string;
    source_url?: string | null;
  }) =>
    apiRequest<KnowledgeSource>('/ai/knowledge-sources', {
      method: 'POST',
      channel: 'platform',
      body: data,
    }),

  syncJournals: () =>
    apiRequest<JournalSyncStatus>('/ai/knowledge-sources/sync-journals', {
      method: 'POST',
      channel: 'platform',
    }),

  contextualPrompt: (action: string, incomingMessage: string, userNote?: string, threadId?: string) =>
    apiRequest<ContextualPromptResult>('/ai/contextual-prompt', {
      method: 'POST',
      channel: 'platform',
      body: {
        action,
        incoming_message: incomingMessage,
        user_note: userNote ?? null,
        thread_id: threadId ?? null,
      },
    }),
};
